// All money arithmetic uses integer cents; projections are estimates, never ledger writes.
const cents = (n) => Math.round(Number(n || 0) * 100);
const stamp = (d) => new Date(`${d}T12:00:00Z`);
export const addDays = (d, n) => { const x = stamp(d); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
export function nextOccurrence(value, unit, every) {
  const d = stamp(value), day = d.getUTCDate();
  if (unit === "day" || unit === "week") return addDays(value, every * (unit === "week" ? 7 : 1));
  d.setUTCDate(1);
  if (unit === "month") d.setUTCMonth(d.getUTCMonth() + every);
  else d.setUTCFullYear(d.getUTCFullYear() + every);
  const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, end));
  return d.toISOString().slice(0, 10);
}

export function forecast(db, current, horizon) {
  const sum = (rows, sign) => rows.reduce((n, r) => n + (r.date <= current ? sign * cents(r.amt) : 0), 0);
  const opening = sum(db.incomes, 1) - sum(db.expenses, 1) - sum(db.savings, 1);
  const start = addDays(current, -29);
  const regular = db.expenses.filter((r) => r.date >= start && r.date <= current && !r.recurring_id);
  const daily = Math.round(regular.reduce((n, r) => n + cents(r.amt), 0) / 30);
  const byDate = {}, end = addDays(current, horizon), currency = db.settings.prefs.currency;
  const posted = new Set();
  [[db.incomes, 1], [db.expenses, -1], [db.savings, -1]].forEach(([rows, sign]) => rows.forEach((r) => {
    if (r.recurring_key) posted.add(r.recurring_key);
    if (r.date > current && r.date <= end) byDate[r.date] = (byDate[r.date] || 0) + sign * cents(r.amt);
  }));
  let mixedCurrency = 0, overdue = 0;
  db.recurring.filter((r) => !r.paused).forEach((r) => {
    if (r.currency && r.currency !== currency) { mixedCurrency++; return; }
    if (r.next <= current) overdue++;
    let next = r.next, count = Number(r.occurrence_count || 0), guard = 0;
    while (next <= end && guard++ < 11000) {
      if ((r.end_date && next > r.end_date) || (r.occurrences && count >= r.occurrences)) break;
      if (next > current && !posted.has(`${r.id}:${next}`)) {
        const sign = r.kind === "income" ? 1 : -1;
        byDate[next] = (byDate[next] || 0) + sign * cents(r.amount);
      }
      next = nextOccurrence(next, r.unit, Number(r.every)); count++;
    }
  });
  let balance = opening;
  const points = Array.from({ length: horizon }, (_, index) => {
    const date = addDays(current, index + 1);
    balance += (byDate[date] || 0) - daily;
    return { date, balance: balance / 100 };
  });
  return { opening: opening / 100, closing: balance / 100, daily: daily / 100, points, mixedCurrency, overdue };
}

export function detectBills(rows, current) {
  const groups = new Map();
  rows.filter((r) => r.date <= current && !r.recurring_id && r.note?.trim()).forEach((r) => {
    const merchant = r.note.trim().toLowerCase().replace(/\s+/g, " ");
    const key = `${r.cat}:${merchant}`;
    groups.set(key, [...(groups.get(key) || []), r]);
  });
  const results = [];
  groups.forEach((values) => {
    // One observation per day prevents duplicate imports from looking like a bill.
    const items = [...new Map(values.map((r) => [r.date, r])).values()].sort((a, b) => a.date.localeCompare(b.date));
    if (items.length < 3) return;
    const last = items.slice(-4), gaps = last.slice(1).map((r, i) => Math.round((stamp(r.date) - stamp(last[i].date)) / 86400000));
    const weekly = gaps.every((n) => n >= 5 && n <= 9), monthly = gaps.every((n) => n >= 26 && n <= 33);
    if (!weekly && !monthly) return;
    const mean = Math.round(last.reduce((n, r) => n + cents(r.amt), 0) / last.length);
    const next = nextOccurrence(last[last.length - 1].date, weekly ? "week" : "month", 1);
    if (next < addDays(current, -35)) return;
    results.push({ name: last[last.length - 1].note, amount: mean / 100, next, unit: weekly ? "week" : "month", cat: last[0].cat });
  });
  return results.sort((a, b) => a.next.localeCompare(b.next));
}

export function safetyNet(db, balances, goals, current, essentialCategories) {
  const start = addDays(current, -89);
  const essential = new Set(essentialCategories);
  const spent = db.expenses.filter((r) => r.date >= start && r.date <= current && essential.has(r.cat)).reduce((n, r) => n + cents(r.amt), 0);
  const monthly = spent / 3 / 100;
  const fund = goals.filter((g) => g.emergency).reduce((n, g) => n + cents(balances[g.id]), 0) / 100;
  return { fund, monthly, months: monthly > 0 ? fund / monthly : null };
}
